import { ReactNode, useEffect, useMemo, useRef } from "react"
import { Circle, MapContainer, TileLayer } from "react-leaflet"
import { LatLngBounds, Map } from "leaflet"
import ScaleNautic from "react-leaflet-nauticsale"
import { GetApp } from "../App"
import { MapLibreTileLayer } from "../Components/MapLibreTileLayer"
import { IAnchorCircle, ITrack, ITrackType } from "../Types"
import { TrackLayerView, TrackViewData } from "./TrackView/Layer"

// Re-exported so existing importers/tests keep resolving the pure geometry helpers
// from `TrackView` after the extraction into `TrackView/Layer`.
export { TrackViewData } from "./TrackView/Layer"

// Anchor tracks (GPS fixes recorded while an anchor watch is active) are styled
// distinctly from the main passage track -- a different color plus a dashed
// stroke, so both color and line style set them apart (also colorblind-safe).
const ANCHOR_TRACK_COLOR = "#8a5a9e";
const ANCHOR_TRACK_DASH = "6 6";

// Swing-circle ring colors, matching the app's existing warning/alert design
// tokens (colors.scss --warning / --alert) so the overlay reads consistently
// with the rest of the anchor-alert UI.
const WARNING_RING_COLOR = "#e7ac52";
const ALARM_RING_COLOR = "#d84532";

interface TrackViewProps {
  fullscreen: boolean;
  noInteraction?: boolean;
  tracks?: ITrack[];
  // Board-time offset in seconds east of UTC (from the track's day/trip timezone).
  // Applied to marker popups and hourly-tick bucketing so times read as board time
  // (Bordzeit) rather than UTC. Defaults to 0 (UTC).
  timezone?: number;
  // Anchor-watch swing circles for the day (day-scope only -- always empty at
  // trip/cruise scope). Each entry draws a warning + alarm ring.
  anchorCircles?: IAnchorCircle[];
}

export function TrackView({ fullscreen, noInteraction, tracks, timezone, anchorCircles }: TrackViewProps) {
  const app = GetApp()!;
  const t = app.getT("TrackView");
  const mapRef = useRef<Map | null>(null);
  const lineWidth = 6;
  const tz = timezone ?? 0;
  const visibleTracks = useMemo(() => tracks?.filter((track) => TrackViewData.hasPositions([track])), [tracks]);
  const visibleAnchorCircles = anchorCircles;

  const [layers, bounds] = useMemo(() => {
    const layers: ReactNode[] = [];
    if (!visibleTracks?.length) return [layers, undefined] as const;
    // Expand the map bounds by the largest ring of each circle too, so a
    // short/just-started anchor track still zooms out to show the full circle.
    const circleBoundsInputs = (visibleAnchorCircles || []).map((c) => ({ lat: c.lat, lng: c.lng, radius: Math.max(c.warningRadius, c.alarmRadius) }));
    const boundsTuple = TrackViewData.computeBounds(visibleTracks, circleBoundsInputs);
    if (visibleTracks) {
      for (const [trackIndex, track] of visibleTracks.entries()) {
        const isAnchor = track.trackType === ITrackType.anchor;
        layers.push(
          <TrackLayerView
            key={`track-${trackIndex}`}
            keyPrefix={`track-${trackIndex}`}
            track={track}
            color={isAnchor ? ANCHOR_TRACK_COLOR : "#5a80a2"}
            dashArray={isAnchor ? ANCHOR_TRACK_DASH : undefined}
            weight={lineWidth}
            timezone={tz}
            markers="full"
            popupLabel={(time) => TrackViewData.popupLabel(time, tz, app.format)}
          />,
        );
      }
    }
    for (const [circleIndex, circle] of (visibleAnchorCircles || []).entries()) {
      // An ended (historical) watch recedes behind the active one: a dashed,
      // lighter stroke/fill, so multiple same-day watches don't visually compete
      // with the currently active circle.
      const historical = !circle.active;
      const ringOptions = { fillOpacity: historical ? 0.02 : 0.05, opacity: historical ? 0.5 : 1, dashArray: historical ? "4 4" : undefined };
      layers.push(
        <Circle key={`anchor-circle-warning-${circleIndex}`} center={[circle.lat, circle.lng]} radius={circle.warningRadius} pathOptions={{ color: WARNING_RING_COLOR, ...ringOptions }} />,
      );
      layers.push(
        <Circle key={`anchor-circle-alarm-${circleIndex}`} center={[circle.lat, circle.lng]} radius={circle.alarmRadius} pathOptions={{ color: ALARM_RING_COLOR, ...ringOptions }} />,
      );
    }
    return [layers, new LatLngBounds([boundsTuple[0][0] ?? 0, boundsTuple[0][1] ?? 0], [boundsTuple[1][0] ?? 0, boundsTuple[1][1] ?? 0])] as const;
  }, [visibleTracks, tz, visibleAnchorCircles]);

  useEffect(() => {
    mapRef.current?.invalidateSize();
  }, [fullscreen]);

  return bounds ? (
    <div className="TrackView">
      {/* Cap the initial fit before layers attach: a stationary track otherwise yields infinite zoom. */}
      <MapContainer bounds={bounds} boundsOptions={{ maxZoom: 16 }} center={bounds.getCenter()} ref={mapRef} scrollWheelZoom={!noInteraction} dragging={!noInteraction}>
        <MapLibreTileLayer attribution='&copy; <a href="https://openfreemap.org/" target="_blank">OpenFreeMap</a>, &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>' url={app.theme == "light" ? "/map-light.json" : "/map.json"} />
        <TileLayer url="http://tiles.openseamap.org/seamark/{z}/{x}/{y}.png" />
        {layers.length > 0 && layers}
        <ScaleNautic imperial={false} metric={false} nautic={true} />
      </MapContainer>
    </div>
  ) : <div className="TrackView-message">{t("no_track")}</div>;
}
